import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Home, 
  FileText, 
  HelpCircle, 
  BookOpen, 
  BarChart3, 
  RotateCcw, 
  Settings,
  Menu,
  X,
  Moon,
  Sun,
  LogOut,
  User,
  Users,
  Crown,
  Briefcase,
  Award,
  ChevronDown,
  ChevronUp,
  Bookmark,
  Heart,
  Lock,
  GraduationCap,
  Sparkles,
  Calendar,
  Building2,
  Brain
} from 'lucide-react';
import { tuitionTeacherService } from '../../firebase/tuition';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';

import Footer from './Footer';
import { useTheme } from '../../contexts/ThemeContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import BookmarkDropdown from '../BookmarkDropdown';
import LanguageSelector from '../LanguageSelector';
import TranslationDisclaimer from '../TranslationDisclaimer';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [teacherStatus, setTeacherStatus] = useState({ isRegistered: false, status: null });
  const [loadingTeacherStatus, setLoadingTeacherStatus] = useState(true);
  const [companionMood, setCompanionMood] = useState('happy'); // happy, sleepy, excited, focused
  const [companionMessage, setCompanionMessage] = useState('');
  const { darkMode, toggleDarkMode } = useTheme();
  const { currentUser } = useRealtime();
  const navigate = useNavigate();

  const location = useLocation();

  // Enhanced device detection with better breakpoints
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setIsDesktop(width >= 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);

    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Handle mobile and tablet-specific interactions
  useEffect(() => {
    if (isMobile || isTablet) {
      // Prevent body scroll when sidebar is open
      if (sidebarOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen, isMobile, isTablet]);

  // Check teacher registration status
  useEffect(() => {
    const checkTeacherStatus = async () => {
      if (!currentUser) {
        setLoadingTeacherStatus(false);
        return;
      }

      try {
        const status = await tuitionTeacherService.getTeacherRegistrationStatus(currentUser.uid);
        setTeacherStatus(status);
      } catch (error) {
        setTeacherStatus({ isRegistered: false, status: null });
      } finally {
        setLoadingTeacherStatus(false);
      }
    };

    checkTeacherStatus();
  }, [currentUser]);

  // Close sidebar when route changes on mobile and tablet
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile, isTablet]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && !event.target.closest('.user-menu')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Update companion mood and messages based on time and activity
  useEffect(() => {
    const updateCompanion = () => {
      const hour = new Date().getHours();
      const messages = {
        happy: ["Keep up the great work! 💪", "You're doing amazing! 🌟", "Stay focused! 📚"],
        sleepy: ["Take a break if needed! 😴", "Stay hydrated! 💧", "You've got this! ⭐"],
        excited: ["Ready to learn! 🚀", "Let's ace this! 🎯", "Time to shine! ✨"],
        focused: ["Deep focus mode! 🎯", "In the zone! 🔥", "Concentrated power! ⚡"]
      };

      if (hour >= 6 && hour < 12) {
        setCompanionMood('excited');
        setCompanionMessage("Good morning! Ready to start? ☀️");
      } else if (hour >= 12 && hour < 17) {
        setCompanionMood('focused');
        setCompanionMessage("Afternoon power! Keep going! 🌞");
      } else if (hour >= 17 && hour < 22) {
        setCompanionMood('happy');
        setCompanionMessage(messages.happy[Math.floor(Math.random() * messages.happy.length)]);
      } else {
        setCompanionMood('sleepy');
        setCompanionMessage("Late night study? Don't forget rest! 🌙");
      }
    };

    updateCompanion();
    const interval = setInterval(() => {
      updateCompanion();
    }, 300000); // Update every 5 minutes

    return () => clearInterval(interval);
  }, []);


  // Check if teacher dashboard should be locked
  const isTeacherDashboardLocked = () => {
    if (loadingTeacherStatus) return true;
    return !teacherStatus.isRegistered || teacherStatus.status !== 'approved';
  };

  // Get user data from realtime context
  const { userData } = useRealtime();

  // Determine user role
  const userRole = userData?.role || userData?.userType || 'student';

  // Role-based navigation
  const getNavigationForRole = () => {
    // Check if user has coaching access
    const hasCoachingAccess = userData?.role === 'admin' || 
                             userData?.role === 'founder' || 
                             userData?.role === 'teacher' ||
                             userData?.coachingId;
    
    const coachingId = userData?.coachingId || 'default-coaching';

    if (userRole === 'teacher') {
      const teacherNav = [
        { name: 'Dashboard', href: '/teacher/dashboard', icon: Home },
        { name: 'Tuition Teacher', href: '/tuition-teachers', icon: GraduationCap },
        { 
          name: 'Teacher Dashboard', 
          href: '/teacher-dashboard', 
          icon: Users, 
          locked: isTeacherDashboardLocked(),
          lockReason: teacherStatus.isRegistered 
            ? teacherStatus.status === 'pending' 
              ? 'Pending admin approval' 
              : teacherStatus.status === 'rejected' 
                ? 'Registration rejected' 
                : 'Not registered as teacher'
            : 'Register as teacher first'
        },
        { name: 'Vacancies', href: '/dashboard/vacancies', icon: Briefcase },
        { name: 'Settings', href: '/dashboard/settings', icon: Settings },
      ];

      // Add coaching management for teachers with coaching access
      if (hasCoachingAccess && userData?.coachingId) {
        teacherNav.splice(1, 0, {
          name: 'Coaching Management',
          href: `/coaching/${coachingId}`,
          icon: Building2,
          description: 'Manage batches and students'
        });
      }

      return teacherNav;
    } else if (userRole === 'institute') {
      return [
        { name: 'Dashboard', href: '/institute/dashboard', icon: Home },
        { name: 'Vacancies', href: '/dashboard/vacancies', icon: Briefcase },
        { name: 'Settings', href: '/dashboard/settings', icon: Settings },
      ];
    } else {
      // Student navigation (default)
      const studentNav = [
        { name: 'Dashboard', href: '/dashboard', icon: Home },
        { name: 'Mock Tests', href: '/dashboard/mock-tests', icon: FileText },
        { name: 'Analysis', href: '/dashboard/analysis', icon: Brain },
        { name: 'Courses', href: '/dashboard/courses', icon: BookOpen },
        { name: 'Tuition Teachers', href: '/tuition-teachers', icon: GraduationCap },
        { 
          name: 'Teacher Dashboard', 
          href: '/teacher-dashboard', 
          icon: Users, 
          locked: isTeacherDashboardLocked(),
          lockReason: teacherStatus.isRegistered 
            ? teacherStatus.status === 'pending' 
              ? 'Pending admin approval' 
              : teacherStatus.status === 'rejected' 
                ? 'Registration rejected' 
                : 'Not registered as teacher'
            : 'Register as teacher first'
        },
        { name: 'Doubts', href: '/dashboard/doubts', icon: HelpCircle },
        { name: 'Test Results', href: '/dashboard/test-results', icon: Award },
        // { name: 'Vacancies', href: '/dashboard/vacancies', icon: Briefcase },
        { name: 'Resources', href: '/dashboard/resources', icon: BookOpen },
        { name: 'Revision', href: '/dashboard/revision', icon: RotateCcw },
        { name: 'Hobbies Hub', href: '/hobbies', icon: Heart },
        { name: 'Settings', href: '/dashboard/settings', icon: Settings },
      ];

      // Add coaching management for admins/founders
      if (hasCoachingAccess && (userData?.role === 'admin' || userData?.role === 'founder')) {
        studentNav.splice(3, 0, {
          name: 'Coaching Management',
          href: `/coaching/${coachingId}`,
          icon: Building2,
          description: 'Manage coaching center'
        });
      }

      return studentNav;
    }
  };

  const navigation = getNavigationForRole();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      // Error signing out
      // Fallback to redirect even if signOut fails
      await signOut(auth).catch(() => {});
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* Translation Disclaimer Banner */}
      <TranslationDisclaimer />
      
      {/* Mobile and tablet sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (isMobile || isTablet) && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div 
        initial={false}
        animate={{ 
          x: sidebarOpen ? 0 : ((isMobile || isTablet) ? -320 : 0),
          width: isMobile ? 320 : (isTablet ? 280 : 260)
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-xl lg:static lg:inset-0 lg:flex-shrink-0 lg:shadow-lg ${
          (isMobile || isTablet) ? 'backdrop-blur-sm' : ''
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <Link to={userRole === 'teacher' ? '/teacher/dashboard' : userRole === 'institute' ? '/institute/dashboard' : '/dashboard'} className="flex items-center space-x-3 notranslate">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">M</span>
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white hidden sm:block notranslate">MahStry</span>
            </Link>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSidebarOpen(false);
              }}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
              aria-label="Close sidebar"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 sm:px-4 py-4 overflow-y-auto">
            <div className="space-y-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                const isLocked = item.locked;
                
                if (isLocked) {
                  const tooltipText = item.lockReason || "Admin hasn't given you access to this section. Please wait.";
                  
                  return (
                    <div
                      key={item.name}
                      className="sidebar-link opacity-60 cursor-not-allowed relative group"
                      title={tooltipText}
                    >
                      <item.icon size={20} className="mr-3 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                      <Lock size={16} className="ml-auto flex-shrink-0" />
                      {/* Tooltip */}
                      <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                        {tooltipText}
                      </div>
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`sidebar-link ${isActive ? 'active' : ''}`}
                    onClick={() => (isMobile || isTablet) && setSidebarOpen(false)}
                  >
                    <item.icon size={20} className="mr-3 flex-shrink-0" />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* MahStry Research Hub Button */}
          <div className="px-3 sm:px-4 pb-4">
            <a
              href="https://mahstry-resarch.web.app"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-pink-500/20 hover:bg-pink-500/30 backdrop-blur-md border border-pink-300/30 text-pink-600 dark:text-pink-400 rounded-lg transition-all duration-300 font-medium text-sm shadow-lg hover:shadow-pink-500/20"
            >
              <span>🔬</span>
              <span>MahStry Research Hub</span>
            </a>
          </div>

          {/* User Section */}
          <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                <User size={18} className="text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {currentUser?.displayName || 'User'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {currentUser?.email || 'user@example.com'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navigation Bar */}
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Left side - Menu button */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
              >
                <Menu size={20} />
              </button>
            </div>

            {/* Center - Virtual Study Companion & Weather Widget */}
            <div className="absolute left-[25%] sm:left-[32%] md:left-1/2 md:transform md:-translate-x-1/2 flex items-center space-x-1.5 sm:space-x-3 md:space-x-6 z-10">
              {/* Virtual Study Companion */}
              <div className="relative group">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative p-1.5 sm:p-2 rounded-full bg-gradient-to-br from-yellow-100 to-orange-100 dark:from-yellow-900/30 dark:to-orange-900/30 border-2 border-yellow-300 dark:border-yellow-700"
                  title={companionMessage}
                >
                  <motion.div
                    animate={{
                      y: [0, -3, 0],
                      rotate: [0, 2, -2, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="text-xl sm:text-2xl"
                  >
                    {companionMood === 'happy' && '😊'}
                    {companionMood === 'sleepy' && '😴'}
                    {companionMood === 'excited' && '🤩'}
                    {companionMood === 'focused' && '🤓'}
                  </motion.div>
                  {/* Sparkle effect */}
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1"
                  >
                    <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-500" />
                  </motion.div>
                </motion.button>
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  {companionMessage}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                  </div>
                </div>
              </div>

            </div>

            {/* Right side - Actions and user menu */}
            <div className="flex items-center space-x-2 sm:space-x-3 relative z-20">
              {/* Language Selector */}
              <LanguageSelector />

              {/* Bookmarks */}
              <BookmarkDropdown />

              {/* Theme toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              {/* User menu */}
              <div className="relative user-menu">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors touch-manipulation"
                >
                  <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                    <User size={16} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  <ChevronDown size={16} className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User dropdown menu */}
                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                    >
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {currentUser?.displayName || 'User'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {currentUser?.email || 'user@example.com'}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
                      >
                        <LogOut size={16} />
                        <span>Logout</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1 overflow-auto">
          <div className="container-mobile pb-4 sm:pb-6 lg:pb-8 pt-2">
            {children}
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default Layout;
